//== navigation for the redemption calculator
//== single goToStep(n) entry point; all back / continue / start-over /
//== tab clicks route through it. focus shifts to the step heading on
//== change so screen reader users land on the new step's title.

import { arm, clearArmed } from './armed.js'

const STEP_COUNT = 5

export function initNavigation(store, root, { resetInputs } = {}) {
  const sections = Array.from(
    root.querySelectorAll('.calc-ui-form-section[data-step]')
  )
  const tabs = Array.from(root.querySelectorAll('.calc-ui-tab[data-step]'))

  let userInitiated = false

  function goToStep(n) {
    const target = clamp(Math.trunc(Number(n)), 0, STEP_COUNT - 1)
    if (Number.isNaN(target)) return
    userInitiated = true
    store.setState({ currentStep: target })
  }

  function render(state) {
    const current = state.currentStep
    const validSteps = state.validSteps ?? new Set([0])

    sections.forEach((el) => {
      const step = Number(el.dataset.step)
      el.hidden = step !== current
    })

    tabs.forEach((el) => {
      const step = Number(el.dataset.step)
      const isActive = step === current
      const reachable = validSteps.has(step)
      //== "is-complete" follows position - any step behind the current one
      //== gets the completed styling, so skipped steps (2 / 3 in early-
      //== redemption mode) still look filled once the user reaches the result.
      //== "is-disabled" is gated on validSteps (not ordering): any non-active
      //== step not in validSteps - a skipped middle step, or a forward step
      //== not yet unlocked. a skipped step therefore carries BOTH classes:
      //== it looks complete but is not clickable.
      const isComplete = step < current
      const isDisabled = !reachable && !isActive

      el.classList.toggle('is-active', isActive)
      el.classList.toggle('is-complete', isComplete)
      el.classList.toggle('is-disabled', isDisabled)
      //== aria-current="step" is the correct primitive for a wizard / stepper
      //== indicator on plain <button> elements; aria-selected is only valid on
      //== role="tab" / option / row etc. Inactive buttons omit the attribute.
      if (isActive) el.setAttribute('aria-current', 'step')
      else el.removeAttribute('aria-current')
      //== unreachable forward tabs: aria-disabled signals the temporary
      //== state to AT, tabindex=-1 takes them out of the keyboard tab order.
      //== don't use the native disabled attribute - the click handler still
      //== needs to fire to arm the current step on a forward-tab attempt.
      if (isDisabled) {
        el.setAttribute('aria-disabled', 'true')
        el.tabIndex = -1
      } else {
        el.removeAttribute('aria-disabled')
        el.tabIndex = 0
      }
    })

    if (userInitiated) {
      const active = sections.find((el) => Number(el.dataset.step) === current)
      const heading = active?.querySelector('.title')
      if (heading) {
        if (!heading.hasAttribute('tabindex')) {
          heading.setAttribute('tabindex', '-1')
        }
        heading.focus({ preventScroll: false })
      }
      userInitiated = false
    }
  }

  //== single delegated click listener for nav buttons and tab strip
  root.addEventListener('click', (event) => {
    const navBtn = event.target.closest('[data-action]')
    if (navBtn && root.contains(navBtn)) {
      const action = navBtn.dataset.action
      const { currentStep, validSteps } = store.getState()
      if (action === 'back') goToStep(prevStep(currentStep, validSteps))
      else if (action === 'continue') {
        const target = nextStep(currentStep, validSteps)
        if (target == null) {
          //== current step isn't valid - arm it so render shows errors
          arm(currentStep)
        } else {
          goToStep(target)
        }
      }
      else if (action === 'start-over') {
        userInitiated = true
        clearArmed()
        store.reset()
        //== restore DOM values + re-seed default-checked radios so the form
        //== matches the cleared store (otherwise stale text and pre-checked
        //== radios make Continue silently no-op when state.product is null)
        resetInputs?.()
      }
      else if (action === 'print') window.print()
      return
    }

    const tab = event.target.closest('.calc-ui-tab[data-step]')
    if (tab && root.contains(tab)) {
      const target = Number(tab.dataset.step)
      const { validSteps, currentStep } = store.getState()
      //== always allow re-selecting the current step
      if (target === currentStep) return
      //== reachability is governed solely by validSteps, which already
      //== excludes steps skipped in early-redemption mode. ordering is not
      //== sufficient: a skipped step (2 / 3) sits before the current step (4).
      if (validSteps?.has(target)) {
        goToStep(target)
      } else {
        //== blocked (forward-unreachable, or a skipped step) - arm the current
        //== step so any validation errors show on a forward attempt
        arm(currentStep)
      }
    }
  })

  store.subscribe(render)
  render(store.getState())

  return { goToStep }
}

function clamp(value, min, max) {
  if (value < min) return min
  if (value > max) return max
  return value
}

//== continue follows validSteps strictly: returns the next reachable step,
//== or null if current step isn't valid (caller arms the step to show errors).
//== back stays permissive - users can always walk backwards even if a later
//== step has gone invalid via edits to earlier steps.
function nextStep(current, validSteps) {
  if (!validSteps || !validSteps.size) return null
  const sorted = [...validSteps].sort((a, b) => a - b)
  const next = sorted.find((s) => s > current)
  return next ?? null
}

function prevStep(current, validSteps) {
  if (validSteps && validSteps.size) {
    const sorted = [...validSteps].sort((a, b) => b - a)
    const prev = sorted.find((s) => s < current)
    if (prev != null) return prev
  }
  return Math.max(current - 1, 0)
}
